import { useCallback, useEffect, useState } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Button, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { AccessEvent, AccessRequest, Member, MappingRow, PortalAccessApiError, portalAccessApiRef } from './api';
import { ApprovalTab, manageableTeams, visibleTabs } from './rules';
import { usePortalAccess } from './usePortalAccess';

const TAB_LABEL: Record<ApprovalTab, string> = { pending: '대기 신청', history: '처리 이력', members: 'team 멤버', approvers: '승인자 지정', mapping: 'mapping' };
const short = (ref?: string | null) => ref?.replace(/^user:default\//, '') ?? '';

/** 재사용: 사유를 받는 버튼 (필수 입력) */
const ReasonAction = ({ label, color, busy, onConfirm }: { label: string; color?: 'primary' | 'secondary'; busy: boolean; onConfirm: (reason: string) => void }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) return <Button size="small" color={color} onClick={() => setOpen(true)}>{label}</Button>;
  return (
    <Box display="inline-flex" alignItems="center" style={{ gap: 8 }}>
      <TextField size="small" variant="outlined" label={`${label} 사유`} inputProps={{ 'aria-label': `${label} 사유` }} value={reason} onChange={e => setReason(e.target.value)} />
      <Button size="small" variant="contained" color={color} disabled={busy || !reason.trim()} onClick={() => { onConfirm(reason.trim()); setOpen(false); setReason(''); }}>{label} 확정</Button>
      <Button size="small" onClick={() => setOpen(false)}>닫기</Button>
    </Box>
  );
};

/** 권한 승인: 대기 신청 · 처리 이력 · team 멤버/회수 · (portal.admin) 승인자 지정 · mapping. 차단은 서버, 탭 노출은 힌트 */
export const AccessApprovalPage = () => {
  const api = useApi(portalAccessApiRef);
  const { me, loading } = usePortalAccess();
  const tabs = visibleTabs(me);
  const [tab, setTab] = useState<ApprovalTab>('pending');
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [team, setTeam] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [approvers, setApprovers] = useState<Member[]>([]);
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [newApprover, setNewApprover] = useState({ team: '', userRef: '', reason: '' });
  const [newMapping, setNewMapping] = useState({ permission: '', groupName: '', reason: '' });
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);

  const teams = manageableTeams(me);
  useEffect(() => {
    if (!team && teams.length) setTeam(teams[0]);
  }, [team, teams]);

  const load = useCallback(() => {
    if (!tabs.includes(tab)) return;
    const run: Record<ApprovalTab, () => Promise<unknown>> = {
      pending: () => api.requests(['requested', 'processing']).then(setRequests),
      history: () => api.events(100).then(setEvents),
      members: () => (team ? api.members(team).then(setMembers) : Promise.resolve()),
      approvers: () => api.approvers().then(setApprovers),
      mapping: () => api.mappings().then(setMappings),
    };
    run[tab]().catch(e => setError((e as Error).message));
  }, [api, tab, team, tabs.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const r = (await fn()) as { message?: string } | undefined;
      setNotice(r?.message ?? '처리했습니다');
    } catch (e) {
      const err = e as PortalAccessApiError;
      // Keycloak 실패는 신청이 `대기` 로 남아 있다 — 같은 버튼으로 재시도할 수 있다
      setError(err.code === 'KEYCLOAK_FAILED' ? `${err.message} (신청은 대기 상태로 남았습니다. 다시 승인을 눌러 재시도하세요)` : err.message);
    } finally {
      setBusy(false);
      load();
    }
  };

  if (loading) return null;
  if (!tabs.length) {
    return (
      <Page themeId="tool">
        <Header title="권한 승인" />
        <Content><Alert severity="warning">승인 권한이 없습니다</Alert></Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="권한 승인" subtitle="승인 즉시 Keycloak group 에 가입되고, 포털 화면에는 catalog 동기화·캐시로 최대 약 6분 뒤 반영되며 새로고침이 필요합니다" />
      <Content>
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        {notice && <Alert severity="success" style={{ marginBottom: 16 }}>{notice}</Alert>}
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} indicatorColor="primary">
          {tabs.map(t => <Tab key={t} value={t} label={TAB_LABEL[t]} />)}
        </Tabs>
        <Paper style={{ padding: 16 }}>
          {tab === 'pending' && (
            <Table size="small" aria-label="대기 신청">
              <TableHead><TableRow><TableCell>신청자</TableCell><TableCell>team</TableCell><TableCell>사유</TableCell><TableCell>직전 오류</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {requests.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{short(r.userRef)}</TableCell>
                    <TableCell>{r.team}</TableCell>
                    <TableCell>{r.reason}</TableCell>
                    <TableCell>{r.lastError ?? ''}</TableCell>
                    <TableCell>
                      <Button size="small" color="primary" variant="contained" disabled={busy} onClick={() => act(() => api.approve(r.id))}>{r.lastError || r.status === 'processing' ? '재시도' : '승인'}</Button>{' '}
                      {/* 처리 중(승인 진행·실패 복구 대기)은 거절할 수 없다 — 재시도로 먼저 확정한다 */}
                      {r.status === 'requested' && <ReasonAction label="거절" color="secondary" busy={busy} onConfirm={reason => act(() => api.reject(r.id, reason))} />}
                    </TableCell>
                  </TableRow>
                ))}
                {!requests.length && <TableRow><TableCell colSpan={5}>대기 중인 신청이 없습니다</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          {tab === 'history' && (
            <>
            <Box component="p" mt={1}>이력은 처리 시점에 기록됩니다. 승인·회수의 결과가 화면 권한에 반영되는 데는 최대 약 6분(catalog 동기화·캐시)이 걸리고 새로고침이 필요합니다.</Box>
            <Table size="small" aria-label="처리 이력">
              <TableHead><TableRow><TableCell>시각</TableCell><TableCell>행위자</TableCell><TableCell>대상</TableCell><TableCell>team/권한</TableCell><TableCell>동작</TableCell><TableCell>사유</TableCell><TableCell>Keycloak</TableCell></TableRow></TableHead>
              <TableBody>
                {events.map(e => (
                  <TableRow key={e.id}>
                    <TableCell>{e.at}</TableCell><TableCell>{short(e.actor)}</TableCell><TableCell>{short(e.targetUser)}</TableCell>
                    <TableCell>{e.subject}</TableCell><TableCell>{e.action}</TableCell><TableCell>{e.reason ?? ''}</TableCell><TableCell>{e.keycloakResult ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
          {tab === 'members' && (
            <>
              <TextField select size="small" variant="outlined" label="team" value={team} onChange={e => setTeam(e.target.value)} style={{ minWidth: 160, marginBottom: 12 }}>
                {teams.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <Table size="small" aria-label="team 멤버">
                <TableHead><TableRow><TableCell>멤버</TableCell><TableCell>이메일</TableCell><TableCell>출처</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {members.map(m => (
                    <TableRow key={m.userRef}>
                      <TableCell>{short(m.userRef)}</TableCell><TableCell>{m.email}</TableCell><TableCell>{m.source}</TableCell>
                      <TableCell><ReasonAction label="회수" color="secondary" busy={busy} onConfirm={reason => act(() => api.revoke(team, m.userRef, reason))} /></TableCell>
                    </TableRow>
                  ))}
                  {!members.length && <TableRow><TableCell colSpan={4}>포털에 기록된 멤버가 없습니다</TableCell></TableRow>}
                </TableBody>
              </Table>
            </>
          )}
          {tab === 'approvers' && (
            <>
              <Box display="flex" style={{ gap: 8, marginBottom: 12 }}>
                <TextField size="small" variant="outlined" label="team (dp·dg·bie 제외)" value={newApprover.team} onChange={e => setNewApprover({ ...newApprover, team: e.target.value })} />
                <TextField size="small" variant="outlined" label="사용자 (user:default/name)" value={newApprover.userRef} onChange={e => setNewApprover({ ...newApprover, userRef: e.target.value })} />
                <TextField size="small" variant="outlined" label="사유" value={newApprover.reason} onChange={e => setNewApprover({ ...newApprover, reason: e.target.value })} />
                <Button color="primary" variant="contained" disabled={busy || !newApprover.team || !newApprover.userRef || !newApprover.reason.trim()}
                  onClick={() => act(() => api.addApprover(newApprover.team, newApprover.userRef, newApprover.reason.trim()).then(() => setNewApprover({ team: '', userRef: '', reason: '' })))}>
                  지정
                </Button>
              </Box>
              <Table size="small" aria-label="승인자">
                <TableHead><TableRow><TableCell>team</TableCell><TableCell>승인자</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {approvers.map(m => (
                    <TableRow key={`${m.groupName}/${m.userRef}`}>
                      <TableCell>{m.groupName.replace(/^approver-/, '')}</TableCell><TableCell>{short(m.userRef)}</TableCell>
                      <TableCell><ReasonAction label="해제" color="secondary" busy={busy} onConfirm={reason => act(() => api.removeApprover(m.groupName.replace(/^approver-/, ''), m.userRef, reason))} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
          {tab === 'mapping' && (
            <>
              <Box display="flex" style={{ gap: 8, marginBottom: 12 }}>
                <TextField size="small" variant="outlined" label="권한 (menu.x · registry.writer · access.approve:team)" style={{ minWidth: 320 }} value={newMapping.permission} onChange={e => setNewMapping({ ...newMapping, permission: e.target.value })} />
                <TextField size="small" variant="outlined" label="group" value={newMapping.groupName} onChange={e => setNewMapping({ ...newMapping, groupName: e.target.value })} />
                <TextField size="small" variant="outlined" label="사유" value={newMapping.reason} onChange={e => setNewMapping({ ...newMapping, reason: e.target.value })} />
                <Button color="primary" variant="contained" disabled={busy || !newMapping.permission || !newMapping.groupName || !newMapping.reason.trim()}
                  onClick={() => act(() => api.addMapping(newMapping.permission, newMapping.groupName, newMapping.reason.trim()).then(() => setNewMapping({ permission: '', groupName: '', reason: '' })))}>
                  추가
                </Button>
              </Box>
              <Table size="small" aria-label="mapping">
              <TableHead><TableRow><TableCell>권한</TableCell><TableCell>group</TableCell><TableCell>등록자</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {mappings.map(m => (
                  <TableRow key={`${m.permission}/${m.groupName}`}>
                    <TableCell>{m.permission}</TableCell><TableCell>{m.groupName}</TableCell><TableCell>{short(m.createdBy)}</TableCell>
                    <TableCell><ReasonAction label="삭제" color="secondary" busy={busy} onConfirm={reason => act(() => api.removeMapping(m.permission, m.groupName, reason))} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
        </Paper>
        <Typography variant="caption" color="textSecondary">화면 노출은 힌트이고, 실제 승인·회수 권한은 서버가 판정합니다.</Typography>
      </Content>
    </Page>
  );
};
